import React, { useState, useEffect } from 'react';
import { Bell, Settings, X, Check, AlertCircle } from 'lucide-react';
import { useFirebaseMessaging } from '../hooks/useFirebaseMessaging';
import { notificationService } from '../services/notificationService';

const NotificationManager = ({ userData, onNotificationReceived }) => {
    const [showSettings, setShowSettings] = useState(false);
    const [notificationHistory, setNotificationHistory] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const {
        messagingToken,
        permission,
        isLoading,
        error,
        requestPermission,
        testNotification,
        lastMessage
    } = useFirebaseMessaging(userData);

    // Handle new messages
    useEffect(() => {
        if (lastMessage) {
            const newNotification = {
                id: Date.now(),
                title: lastMessage.notification?.title || 'Thông báo mới',
                body: lastMessage.notification?.body || '',
                data: lastMessage.data || {},
                timestamp: new Date(),
                read: false
            };

            setNotificationHistory(prev => [newNotification, ...prev.slice(0, 49)]); // Keep only 50 recent
            setUnreadCount(prev => prev + 1);

            // Notify parent component
            if (onNotificationReceived) {
                onNotificationReceived(newNotification);
            }

            // Handle specific notification types
            handleNotificationAction(newNotification);
        }
    }, [lastMessage, onNotificationReceived]);

    // Subscribe to notification service events
    useEffect(() => {
        const unsubscribeShipment = notificationService.subscribe('shipment_assigned', (data) => {
            notificationService.showInAppNotification(
                'Chuyến xe mới',
                `Bạn có chuyến xe mới #${data.shipmentId}`,
                'info'
            );
        });

        const unsubscribeStatus = notificationService.subscribe('shipment_status_changed', (data) => {
            notificationService.showInAppNotification(
                'Cập nhật trạng thái',
                `Chuyến xe #${data.shipmentId} đã được cập nhật`,
                'success'
            );
        });

        const unsubscribeUrgent = notificationService.subscribe('urgent_message', (data) => {
            notificationService.showInAppNotification(
                'Thông báo khẩn',
                data.message,
                'warning'
            );
        });

        return () => {
            unsubscribeShipment();
            unsubscribeStatus();
            unsubscribeUrgent();
        };
    }, []);

    const handleNotificationAction = (notification) => {
        const { data } = notification;
        
        switch (data.type) {
            case 'shipment_assigned':
                // Auto navigate to shipment detail if app is open
                if (data.shipmentId && onNotificationReceived) {
                    onNotificationReceived({
                        ...notification,
                        action: 'navigate',
                        target: `/shipment/${data.shipmentId}`
                    });
                }
                break;
            
            case 'urgent_message':
                // Show urgent notification
                notificationService.showInAppNotification(
                    'Thông báo khẩn',
                    notification.body,
                    'warning',
                    10000 // Show for 10 seconds
                );
                break;
        }
    };

    const markAsRead = (notificationId) => {
        setNotificationHistory(prev => 
            prev.map(n => 
                n.id === notificationId ? { ...n, read: true } : n
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
        setNotificationHistory(prev => 
            prev.map(n => ({ ...n, read: true }))
        );
        setUnreadCount(0);
    };

    const clearHistory = () => {
        setNotificationHistory([]);
        setUnreadCount(0);
    };

    const formatTime = (timestamp) => {
        const now = new Date();
        const time = new Date(timestamp);
        const diff = now - time;
        
        if (diff < 60000) return 'Vừa xong';
        if (diff < 3600000) return `${Math.floor(diff / 60000)} phút trước`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)} giờ trước`;
        return time.toLocaleDateString('vi-VN');
    };

    const getPermissionText = () => {
        switch (permission) {
            case 'granted': return 'Đã bật';
            case 'denied': return 'Bị từ chối';
            default: return 'Chưa thiết lập';
        }
    };

    const getPermissionColor = () => {
        switch (permission) {
            case 'granted': return 'text-green-600';
            case 'denied': return 'text-red-600';
            default: return 'text-yellow-600';
        }
    };

    return (
        <>
            {/* Notification Bell Icon */}
            <div className="relative">
                <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <Bell className="w-6 h-6" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>

                {/* Notification Panel */}
                {showSettings && (
                    <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-hidden">
                        {/* Header */}
                        <div className="p-4 border-b bg-gray-50">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900">Thông báo</h3>
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Permission Status */}
                        <div className="p-4 border-b">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-600">Trạng thái thông báo:</span>
                                <span className={`text-sm font-medium ${getPermissionColor()}`}>
                                    {getPermissionText()}
                                </span>
                            </div>
                            
                            {permission !== 'granted' && (
                                <button
                                    onClick={requestPermission}
                                    disabled={isLoading}
                                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm"
                                >
                                    {isLoading ? 'Đang xử lý...' : 'Bật thông báo'}
                                </button>
                            )}

                            {permission === 'granted' && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={testNotification}
                                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm"
                                    >
                                        Thử nghiệm
                                    </button>
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={markAllAsRead}
                                            className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                                        >
                                            Đọc tất cả
                                        </button>
                                    )}
                                </div>
                            )}

                            {error && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                                    <AlertCircle className="w-4 h-4 inline mr-1" />
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* Notification History */}
                        <div className="max-h-64 overflow-y-auto">
                            {notificationHistory.length > 0 ? (
                                <div className="divide-y">
                                    {notificationHistory.map((notification) => (
                                        <div
                                            key={notification.id}
                                            className={`p-4 hover:bg-gray-50 cursor-pointer ${
                                                !notification.read ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                            }`}
                                            onClick={() => markAsRead(notification.id)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-sm text-gray-900">
                                                        {notification.title}
                                                    </h4>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        {notification.body}
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-2">
                                                        {formatTime(notification.timestamp)}
                                                    </p>
                                                </div>
                                                {!notification.read && (
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Chưa có thông báo nào</p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {notificationHistory.length > 0 && (
                            <div className="p-4 border-t bg-gray-50">
                                <button
                                    onClick={clearHistory}
                                    className="w-full text-center text-sm text-gray-600 hover:text-gray-800"
                                >
                                    Xóa tất cả thông báo
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Debug Info (Development only) */}
            {/* {process.env.NODE_ENV === 'development' && messagingToken && (
                <div className="fixed bottom-4 left-4 bg-black text-white p-2 rounded text-xs max-w-xs opacity-75">
                    <div>FCM: {messagingToken.substring(0, 20)}...</div>
                    <div>Permission: {permission}</div>
                    <div>Unread: {unreadCount}</div>
                </div>
            )} */}
        </>
    );
};

export default NotificationManager;